import { useCallback, useState } from "react";

import { ApiError } from "../api/client";
import { blockChat, getBlockedChatPreview, resolveChat, unblockChat } from "../api/endpoints";
import { describeError } from "../api/errors";
import type { BlockedChatPreview, ScraperChat } from "../api/types";

/**
 * done        - the chat is on monitoring again
 * not_blocked - there was nothing to put back
 */
export type ChatUnblockResult = "done" | "not_blocked";

interface ChatBlockState {
  resolved: ScraperChat | null;
  preview: BlockedChatPreview | null;
  blocked: BlockedChatPreview | null;
  unblocked: ChatUnblockResult | null;
  working: boolean;
  /**
   * Whether the unblocking call is in flight, separately from ``working``.
   *
   * Two flags and not one because the screen runs two independent sections off this hook. With a
   * shared flag, reading what a block would cost puts "Выполняем…" on the button that puts a chat
   * back - a button which at that moment is doing nothing at all.
   */
  unblocking: boolean;
  error: string | null;
  /** What went wrong while unblocking, kept apart from ``error`` so each section shows its own. */
  unblockError: string | null;
}

export interface UseChatBlock extends ChatBlockState {
  resolve: (row: string) => void;
  look: (chatId: number) => void;
  block: (chatId: number, reason: string) => void;
  unblock: (chatId: number) => void;
  reset: () => void;
}

/** A chat id as an operator types it: digits, optionally negative, and not zero. */
export function parseChatId(value: string): number | null {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return null;
  }
  const chatId = Number(trimmed);
  // supergroups and channels are -100..., so negative is the normal case and not a typo
  return Number.isSafeInteger(chatId) && chatId !== 0 ? chatId : null;
}

const EMPTY: ChatBlockState = {
  resolved: null,
  preview: null,
  blocked: null,
  unblocked: null,
  working: false,
  unblocking: false,
  error: null,
  unblockError: null,
};

/**
 * Taking a chat off monitoring and putting it back.
 *
 * Blocking is three steps: resolve a link, read what it would cost, act. They are separate calls
 * rather than one screen state because the counts have to come from the server at the moment the
 * operator is deciding - a preview fetched when the chat was resolved could be minutes old by then.
 *
 * The resolve lives here and not in useScraperSearch: the two sections of that screen are opposite
 * actions on a chat and must not share a resolved chat, or adding a scraper somewhere would arm
 * the button that takes it off monitoring.
 *
 * Unblocking is one step and takes a raw chat_id: by then the scraper has left the chat, so a link
 * to it may no longer resolve, and a chat nobody can resolve would be a chat nobody can put back.
 */
export function useChatBlock(): UseChatBlock {
  const [state, setState] = useState<ChatBlockState>(EMPTY);

  /** Find the chat this section is about. Its counts belong to that chat, so they go. */
  const resolve = useCallback(async (row: string) => {
    const blank = { resolved: null, preview: null, blocked: null, error: null };
    setState((previous) => ({ ...previous, ...blank, working: true }));
    try {
      const chat = await resolveChat(row);
      setState((previous) => ({ ...previous, ...blank, resolved: chat, working: false }));
    } catch (error) {
      setState((previous) => ({ ...previous, ...blank, working: false, error: describeError(error) }));
    }
  }, []);

  const look = useCallback(async (chatId: number) => {
    setState((previous) => ({ ...previous, preview: null, blocked: null, working: true, error: null }));
    try {
      const { preview } = await getBlockedChatPreview(chatId);
      setState((previous) => ({ ...previous, preview, working: false }));
    } catch (error) {
      setState((previous) => ({ ...previous, working: false, error: describeError(error) }));
    }
  }, []);

  const block = useCallback(async (chatId: number, reason: string) => {
    setState((previous) => ({ ...previous, working: true, error: null }));
    try {
      await blockChat(chatId, reason);
      // the preview is kept and shown as the result: what was confirmed is what happened, and
      // re-reading it now would only show zeroes
      setState((previous) => ({
        ...previous,
        preview: null,
        blocked: previous.preview,
        working: false,
        error: null,
      }));
    } catch (error) {
      setState((previous) => ({ ...previous, working: false, error: describeError(error) }));
    }
  }, []);

  const unblock = useCallback(async (chatId: number) => {
    setState((previous) => ({ ...previous, unblocked: null, unblocking: true, unblockError: null }));
    try {
      await unblockChat(chatId);
      setState((previous) => ({ ...previous, unblocked: "done", unblocking: false }));
    } catch (error) {
      // not being blocked is the state the operator asked for, so it is an answer and not a
      // failure
      if (error instanceof ApiError && error.status === 404) {
        setState((previous) => ({ ...previous, unblocked: "not_blocked", unblocking: false }));
        return;
      }
      setState((previous) => ({ ...previous, unblocking: false, unblockError: describeError(error) }));
    }
  }, []);

  /**
   * Step back from a confirmation, keeping the chat that was resolved.
   *
   * The resolve stays on purpose: this is what the Отмена button calls, and an operator who
   * decided not to block this chat yet should not have to search for it again. It touches the
   * blocking half only - the other section's answer is not this button's to clear.
   */
  const reset = useCallback(() => {
    setState((previous) => ({ ...previous, preview: null, blocked: null, working: false, error: null }));
  }, []);

  return {
    ...state,
    resolve: (row: string) => void resolve(row),
    look: (chatId: number) => void look(chatId),
    block: (chatId: number, reason: string) => void block(chatId, reason),
    unblock: (chatId: number) => void unblock(chatId),
    reset,
  };
}
